import { GripVertical, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ImagePicker } from '@/components/common/image-picker';
import { MarkdownEditor } from '@/components/common/markdown-editor';
import { TokenField } from '@/components/common/token-field';
import { PreferenceRow } from '@/components/settings/settings-controls';
import { cn } from '@/lib/utils';

/** Long edge the portrait is downscaled to; the server re-encodes at the same bound. */
export const BIO_PHOTO_MAX_DIM = 1024;
/** Mirrors staffBioMaxRunes on the server, which 400s past it. */
export const BIO_MAX_LENGTH = 8000;
/** Mirrors the server's staffHeadlineMaxRunes / staffQuoteMaxRunes. */
export const HEADLINE_MAX_LENGTH = 80;
export const QUOTE_MAX_LENGTH = 240;
export const AWARDS_MAX_ITEMS = 10;

function Field({ label, children, hint, htmlFor, optional }) {
  const Label = htmlFor ? 'label' : 'span';
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {label}
        {optional ? (
          <span className="ml-1 font-normal normal-case tracking-normal">— optional</span>
        ) : null}
      </Label>
      {children}
      {hint ? <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/**
 * The structured doctor profile form.
 *
 * Presentational only — it takes values plus onChange callbacks and knows nothing about
 * persistence, so the dialog keeps owning the hydrate gate, the baseline diffing and the save.
 *
 * The taxonomies exist as typed fields rather than markdown headings because the patient app lays
 * each one out differently (chips, a middot rule, a two-column fact list); leaving them in the bio
 * prose is what made the old screen read like a pasted web page.
 */
export function ProfileEditor({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  photo,
  onPhotoChange,
  headline,
  onHeadlineChange,
  specialties,
  onSpecialtiesChange,
  qualifications,
  onQualificationsChange,
  clinicalInterests,
  onClinicalInterestsChange,
  languages,
  onLanguagesChange,
  bio,
  onBioChange,
  quote,
  onQuoteChange,
  awards,
  onAwardsChange,
  orderIndex,
  onOrderIndexChange,
  childrensCare,
  onChildrensCareChange,
  disabled = false,
}) {
  const patchAward = (index, partial) => {
    onAwardsChange?.(awards.map((award, i) => (i === index ? { ...award, ...partial } : award)));
  };
  const moveAward = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= awards.length) return;
    const next = [...awards];
    [next[index], next[target]] = [next[target], next[index]];
    onAwardsChange?.(next);
  };

  return (
    <div className="space-y-5">
      {/* Identity: portrait beside the name/headline/specialties stack. */}
      <div className="flex items-start gap-4">
        <div className="w-40 shrink-0">
          <ImagePicker
            value={photo}
            onChange={onPhotoChange}
            shape="portrait"
            previewClassName="aspect-[3/4] w-40"
            maxDim={BIO_PHOTO_MAX_DIM}
            label="Portrait"
            hint="3:4 portrait, ≥ 900px tall. Head-and-shoulders, plain light background. A PNG with the background removed looks best — it is shown full-bleed in the app and on the patient's home screen."
            emptyLabel="Drop, paste or click"
            disabled={disabled}
          />
        </div>
        <div className="min-w-0 flex-1 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" htmlFor="staff-first-name">
              <Input
                id="staff-first-name"
                value={firstName}
                onChange={(e) => onFirstNameChange?.(e.target.value)}
                placeholder="Jason"
                disabled={disabled}
              />
            </Field>
            <Field label="Last name" htmlFor="staff-last-name">
              <Input
                id="staff-last-name"
                value={lastName}
                onChange={(e) => onLastNameChange?.(e.target.value)}
                placeholder="Lee"
                disabled={disabled}
              />
            </Field>
          </div>
          <Field
            label="Headline"
            htmlFor="staff-headline"
            hint="Sits above the name in the app. Keep it under 40 characters."
          >
            <Input
              id="staff-headline"
              value={headline}
              onChange={(e) => onHeadlineChange?.(e.target.value)}
              maxLength={HEADLINE_MAX_LENGTH}
              placeholder="Medical Director & Senior Consultant"
              disabled={disabled}
            />
          </Field>
          <Field
            label="Specialties"
            hint="Shown as blue chips. Also drives search and the booking directory filter."
          >
            <TokenField
              value={specialties}
              onChange={onSpecialtiesChange}
              variant="primary"
              placeholder="Add specialty…"
              disabled={disabled}
            />
          </Field>
        </div>
      </div>

      <Separator />

      {/* Directory: how this doctor is listed and filtered in the patient app's booking
          surfaces — not rendered on the profile itself, so no preview change. */}
      <div className="grid grid-cols-2 items-start gap-3">
        <Field
          label="Order number"
          htmlFor="staff-order-index"
          optional
          hint="Position in the app's doctor list — lower numbers first. Leave empty to list after numbered doctors, alphabetically."
        >
          <Input
            id="staff-order-index"
            inputMode="numeric"
            pattern="[0-9]*"
            value={orderIndex}
            onChange={(e) => onOrderIndexChange?.(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 1"
            disabled={disabled}
          />
        </Field>
        <div className="rounded-lg border border-border/70 bg-muted/20">
          <PreferenceRow
            title="Children's eye care provider"
            description="Shown in — and filtered into — the children's eye care booking flow."
            checked={Boolean(childrensCare)}
            onChange={(next) => onChildrensCareChange?.(next)}
            disabled={disabled}
            className="px-3 py-2.5"
          />
        </div>
      </div>

      <Separator />

      <Field
        label="Qualifications"
        hint="Order is preserved; the app joins them with middots on one rule-bounded line."
      >
        <TokenField
          value={qualifications}
          onChange={onQualificationsChange}
          placeholder="Add qualification…"
          disabled={disabled}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Clinical interests">
          <TokenField
            value={clinicalInterests}
            onChange={onClinicalInterestsChange}
            placeholder="Add…"
            disabled={disabled}
          />
        </Field>
        <Field label="Spoken languages">
          <TokenField
            value={languages}
            onChange={onLanguagesChange}
            placeholder="Add…"
            disabled={disabled}
          />
        </Field>
      </div>

      <Separator />

      <Field
        label="About"
        hint="Plain paragraphs read best — the app styles the first one as a lead. Headings are supported but rarely needed now that the fields above are structured."
      >
        <MarkdownEditor
          value={bio}
          onChange={onBioChange}
          maxLength={BIO_MAX_LENGTH}
          rows={9}
          disabled={disabled}
          placeholder={'A short introduction.\n\nMarkdown works: **bold**, *italic*, - lists, [links](https://example.com).'}
        />
      </Field>

      <Field
        label="Pull quote"
        htmlFor="staff-quote"
        optional
        hint="One sentence in the doctor's own voice. Hidden in the app when empty."
      >
        <Input
          id="staff-quote"
          value={quote}
          onChange={(e) => onQuoteChange?.(e.target.value)}
          maxLength={QUOTE_MAX_LENGTH}
          placeholder="Every patient has the right to good sight."
          disabled={disabled}
        />
      </Field>

      <Field label="Recognition">
        <div className="flex flex-col gap-2">
          {awards.map((award, index) => (
            <div
              key={index}
              className="grid grid-cols-[16px_1fr_150px_26px] items-center gap-2"
            >
              {/* Reordering is ↑/↓ rather than drag: no dnd utility exists in this repo, and the
                  README says to ship buttons rather than add a dependency for it. */}
              <div className="flex flex-col items-center text-muted-foreground/50">
                <button
                  type="button"
                  aria-label={`Move ${award.title || 'award'} up`}
                  disabled={disabled || index === 0}
                  onClick={() => moveAward(index, -1)}
                  className="leading-none transition hover:text-foreground disabled:opacity-30"
                >
                  <GripVertical className="h-3.5 w-3.5 rotate-90" aria-hidden />
                </button>
              </div>
              <Input
                value={award.title}
                onChange={(e) => patchAward(index, { title: e.target.value })}
                placeholder="Award or recognition"
                disabled={disabled}
              />
              <Input
                value={award.note}
                onChange={(e) => patchAward(index, { note: e.target.value })}
                placeholder="Year or citation"
                disabled={disabled}
              />
              <button
                type="button"
                aria-label={`Remove ${award.title || 'award'}`}
                disabled={disabled}
                onClick={() => onAwardsChange?.(awards.filter((_, i) => i !== index))}
                className={cn(
                  'grid h-[26px] w-[26px] place-items-center rounded-md text-muted-foreground transition',
                  'hover:bg-destructive/10 hover:text-destructive disabled:opacity-40',
                )}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || awards.length >= AWARDS_MAX_ITEMS}
          onClick={() => onAwardsChange?.([...awards, { title: '', note: '' }])}
          className="mt-2.5 h-8 border-dashed px-3 text-xs font-semibold"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add award
        </Button>
      </Field>
    </div>
  );
}
